import { guardAdminOnly } from "@/lib/auth/routeGuards"
import { useState, useEffect, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { Home, ArrowLeftCircle, Sparkles, Table, Menu, X } from 'lucide-react'
import { createFileRoute } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Overview } from '@/components/adminPage/timetable/Overview'
import TimetableGenerator from '@/components/adminPage/timetable/TimetableGenerator'
import { TimetableDisplay } from '@/components/adminPage/timetable/TimetableDisplay'
import { useToast } from '@/components/ui/use-toast'
import { Button } from '@/components/ui/button'
import { TimetableResult } from '@/lib/api/services/timetable-service'
import { dashboardTimetableService } from '@/lib/api/services/dashboard-timetable-service'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAdminStore } from '@/store/adminStore'
import { TimetableAdminTour } from '@/tour/admin/timetableAdminTour'

export const Route = createFileRoute('/admin/timetable')({
  beforeLoad: guardAdminOnly,
  component: RouteComponent,
  loader: async () => {
    return null
  },
})

export default function RouteComponent() {
  const { t } = useTranslation()
  const [activeComponent, setActiveComponent] = useState('Overview')
  const [activeSubComponent, setActiveSubComponent] = useState('')
  const [generatedTimetable, setGeneratedTimetable] =
    useState<TimetableResult | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  // Add tour state
  const { timetableTour, startTimetableTour, resetTimetableTour } = useAdminStore()

  // Function to check if there's valid timetable data
  const hasValidTimetableData = () => {
    if (!generatedTimetable) return false
    
    // Check if there's scheduleData with actual classes
    if (generatedTimetable.scheduleData && Array.isArray(generatedTimetable.scheduleData)) {
      return generatedTimetable.scheduleData.length > 0
    }
    
    // Check if there's schedule with actual classes
    if (generatedTimetable.schedule && typeof generatedTimetable.schedule === 'object') {
      const classes = Object.keys(generatedTimetable.schedule)
      return classes.length > 0
    }
    
    return false
  }

  const loadExistingTimetable = useCallback(async () => {
    try {
      setIsLoading(true)
  // console.logLoading existing timetable from route component...')
      
      const timetables =
        await dashboardTimetableService.getCurrentEstablishmentTimetables()

  // console.logTimetables loaded:', timetables)

      if (timetables && timetables.length > 0) {
        const activeTimetable =
          timetables.find((t) => t.isActive) || timetables[0]
        
    // console.logSelected timetable:', activeTimetable)
        
        let timetableData: TimetableResult = { scheduleData: [], stats: {} }

        // Check if the data is in the new format (data.data.scheduleData)
        if (activeTimetable.data && activeTimetable.data.scheduleData) {
      // console.logFound scheduleData in new format')
          timetableData.scheduleData = activeTimetable.data.scheduleData
          if (activeTimetable.data.stats) {
            timetableData.stats = activeTimetable.data.stats
          }
        }
        // Check if the data is in the old format (data.data as string)
        else if (typeof activeTimetable.data === 'string') {
          try {
        // console.logParsing timetable data from string')
            const parsedData = JSON.parse(activeTimetable.data)
            if (parsedData.scheduleData) {
              timetableData.scheduleData = parsedData.scheduleData
            } else if (parsedData.schedule) {
              timetableData.schedule = parsedData.schedule
            }
            if (parsedData.stats) {
              timetableData.stats = parsedData.stats
            }
          } catch (e) {
            // console.error('Error parsing timetable data:', e)
          }
        } 
        // Check if the data is in object format
        else if (activeTimetable.data) {
      // console.logProcessing timetable data object')
          if (activeTimetable.data.scheduleData) {
            timetableData.scheduleData = activeTimetable.data.scheduleData
          } else if (activeTimetable.data.schedule) {
            timetableData.schedule = activeTimetable.data.schedule
          }
          if (activeTimetable.data.stats) {
            timetableData.stats = activeTimetable.data.stats
          }
        }

    // console.logFinal timetable data:', timetableData)
        
        // Only set the timetable if we have valid data
        if (timetableData.scheduleData && timetableData.scheduleData.length > 0) {
          setGeneratedTimetable(timetableData)
      // console.logTimetable data set successfully')
        } else {
      // console.logNo valid timetable data found')
          setGeneratedTimetable(null)
        }
      } else {
    // console.logNo timetables found')
        setGeneratedTimetable(null)
      }
    } catch (error) {
      // console.error('Error loading existing timetable:', error)
      setGeneratedTimetable(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Keep the initial load on mount
  useEffect(() => {
    loadExistingTimetable()
  }, [loadExistingTimetable])

  // Refetch when navigating to DisplayTimetable
  useEffect(() => {
    if (activeComponent === 'DisplayTimetable' || activeSubComponent === 'DisplayTimetable') {
      loadExistingTimetable()
    }
  }, [activeComponent, activeSubComponent, loadExistingTimetable])

  useEffect(() => {
    const handleNavigateToGenerator = () => {
      setActiveComponent('TimetableGenerator')
    }

    const handleNavigateToDisplay = () => {
      if (hasValidTimetableData()) {
        setActiveComponent('DisplayTimetable')
      } else {
        setActiveComponent('TimetableGenerator')
        toast({
          title: t(
            'routes.admin-folder.timetable.toasts.noTimetableFound.title'
          ),
          description: t(
            'routes.admin-folder.timetable.toasts.noTimetableFound.description'
          ),
          variant: 'default',
        })
      }
    }

    const handleShowToast = (event: CustomEvent) => {
      if (event.detail) {
        toast({
          title: event.detail.title,
          description: event.detail.description,
          variant: 'default',
        })
      }
    }

    window.addEventListener(
      'navigate-timetable-generator',
      handleNavigateToGenerator
    )
    window.addEventListener(
      'navigate-timetable-display',
      handleNavigateToDisplay
    )
    window.addEventListener('show-toast', handleShowToast as EventListener)

    return () => {
      window.removeEventListener(
        'navigate-timetable-generator',
        handleNavigateToGenerator
      )
      window.removeEventListener(
        'navigate-timetable-display',
        handleNavigateToDisplay
      )
      window.removeEventListener('show-toast', handleShowToast as EventListener)
    }
  }, [generatedTimetable, toast, t])

  const handleTimetableGenerated = (data: TimetableResult) => {
    setGeneratedTimetable(data)
    setActiveComponent('DisplayTimetable')
    toast({
      title: t('routes.admin-folder.timetable.toasts.timetableGenerated.title'),
      description: t(
        'routes.admin-folder.timetable.toasts.timetableGenerated.description'
      ),
    })
  }

  const formatTimetableForDisplay = (data: TimetableResult | null) => {
    if (!data) return null

    if (
      data.scheduleData &&
      Array.isArray(data.scheduleData) &&
      data.scheduleData.length > 0
    ) {
      return {
        name: t('routes.admin-folder.timetable.generatedTimetableName'),
        scheduleData: data.scheduleData,
        stats: data.stats || {
          classes: 0,
          teachers: 0,
          rooms: 0,
          periods: 0,
          days: 0,
          assignments: 0,
        },
      }
    }

    const formattedData = {
      name: t('routes.admin-folder.timetable.generatedTimetableName'),
      schedule: {} as Record<string, Array<any>>,
      stats: data.stats || {
        classes: 0,
        teachers: 0,
        rooms: 0,
        periods: 0,
        days: 0,
        assignments: 0,
      },
    }

    if (data.schedule) {
      Object.entries(data.schedule).forEach(([className, assignments]) => {
        formattedData.schedule[className] = assignments.map(
          (assignment: any) => {
            return {
              subject: assignment.subject,
              teacher: assignment.teacher,
              room: assignment.room || 'Not specified',
              day: assignment.day,
              period: assignment.period || 1,
              time:
                assignment.time ||
                `${assignment.period} => ${assignment.period + 1}`,
              ...(assignment.group1 ? { group1: assignment.group1 } : {}),
              ...(assignment.group2 ? { group2: assignment.group2 } : {}),
            }
          }
        )
      })
    }

    return formattedData
  }

  const handleDeleteTimetable = async () => {
    if (!generatedTimetable) {
      toast({
        title: t(
          'routes.admin-folder.timetable.toasts.noTimetableToDelete.title'
        ),
        description: t(
          'routes.admin-folder.timetable.toasts.noTimetableToDelete.description'
        ),
        variant: 'destructive',
      })
      return
    }

    try {
      toast({
        title: t('routes.admin-folder.timetable.toasts.timetableDeleted.title'),
        description: t(
          'routes.admin-folder.timetable.toasts.timetableDeleted.description'
        ),
      })

      setGeneratedTimetable(null)
      setActiveComponent('Overview')

      try {
        const timetables =
          await dashboardTimetableService.getCurrentEstablishmentTimetables()
        const activeTimetable = timetables.find((t) => t.isActive)

        if (activeTimetable) {
          await dashboardTimetableService.deleteTimetable(activeTimetable.id)
          queryClient.invalidateQueries({ queryKey: ['active-timetable'] })
        }
      } catch (serverError) {
        // console.error('Error deleting timetable from server:', serverError)
      }
    } catch (error) {
      // console.error('Error in delete operation:', error)
      toast({
        title: t('routes.admin-folder.timetable.toasts.deleteError.title'),
        description: t(
          'routes.admin-folder.timetable.toasts.deleteError.description'
        ),
        variant: 'destructive',
      })
    }
  }

  const componentsMap: { [key: string]: JSX.Element } = {
    Overview: <Overview />,
    TimetableGenerator: (
      <TimetableGenerator onTimetableGenerated={handleTimetableGenerated} />
    ),
    DisplayTimetable: isLoading ? (
      <div className="flex flex-col items-center justify-center h-[80vh] space-y-4">
        <h2 className="text-2xl font-bold">
          {t('routes.admin-folder.timetable.timetableLoading')}
        </h2>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    ) : generatedTimetable ? (
      (() => {
    // console.logRendering TimetableDisplay with data:', generatedTimetable)
        const formattedData = formatTimetableForDisplay(generatedTimetable)
    // console.logFormatted data for TimetableDisplay:', formattedData)
        return (
          <TimetableDisplay
            timetableData={formattedData}
            onBack={() => setActiveComponent('TimetableGenerator')}
            onRegenerate={() => setActiveComponent('TimetableGenerator')}
            onDelete={handleDeleteTimetable}
          />
        )
      })()
    ) : (
      <div className="flex flex-col items-center justify-center h-[80vh] space-y-4">
        <h2 className="text-2xl font-bold">
          {t('routes.admin-folder.timetable.noTimetable.title')}
        </h2>
        <p className="text-gray-500">
          {t('routes.admin-folder.timetable.noTimetable.message')}
        </p>
        <Button onClick={() => setActiveComponent('TimetableGenerator')}>
          {t('routes.admin-folder.timetable.noTimetable.button')}
        </Button>
      </div>
    ),
  }

  function goBack() {
    navigate({ to: '/admin' })
  }

  // Function to handle starting the tour
  const handleStartTour = () => {
    resetTimetableTour()
    setTimeout(() => {
      startTimetableTour()
    }, 100)
  }

  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Mobile menu button */}
        <div className="lg:hidden flex items-center justify-between p-4 bg-gray-100 border-b border-gray-200">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg text-gray-700 hover:bg-gray-200"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
          <h2 className="text-lg font-semibold">
            {t('routes.admin-folder.timetable.title')}
          </h2>
        </div>

        {/* Sidebar */}
        <div
          className={cn(
            'h-screen lg:h-auto w-64 bg-gray-100 border-r border-gray-200 flex flex-col fixed lg:static z-50 transition-transform duration-300 ease-in-out',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          )}
        >
          <Sidebar
            setActiveComponent={(component) => {
              setActiveComponent(component)
              setSidebarOpen(false) // Close sidebar on tablet after selection
            }}
            setActiveSubComponent={setActiveSubComponent}
            goBack={goBack}
            activeComponent={activeComponent}
            activeSubComponent={activeSubComponent}
            hasGeneratedTimetable={hasValidTimetableData()}
            isLoading={isLoading}
            onStartTour={handleStartTour}
            tourCompleted={timetableTour?.hasCompleted}
          />
        </div>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <div className="flex-1 p-4 md:p-6 min-w-0">
          {componentsMap[activeSubComponent || activeComponent]}
        </div>
        <TimetableAdminTour />
      </div>
    </DndProvider>
  )
}

function Sidebar({
  setActiveComponent,
  setActiveSubComponent,
  goBack,
  activeComponent,
  hasGeneratedTimetable,
  isLoading,
}: {
  setActiveComponent: (component: string) => void
  setActiveSubComponent: (component: string) => void
  goBack: () => void
  activeComponent: string
  activeSubComponent: string
  hasGeneratedTimetable: boolean
  isLoading: boolean
  onStartTour: () => void
  tourCompleted: boolean
}) {
  const { t } = useTranslation()

  const navItems = [
    {
      label: t('routes.admin-folder.timetable.sidebar.dashboard'),
      component: 'Overview',
      icon: Home,
    },
    {
      label: t('routes.admin-folder.timetable.sidebar.timetableGenerator'),
      component: 'TimetableGenerator',
      icon: Sparkles,
    },
    {
      label: t('routes.admin-folder.timetable.sidebar.viewGeneratedTimetable'),
      component: 'DisplayTimetable',
      icon: Table,
    },
  ]

  return (
    <div
      className={cn(
        'h-full w-64 bg-gray-100 border-r border-gray-200 flex flex-col overflow-y-auto'
      )}
    >
      <div className="p-4 space-y-4">
        <button
          onClick={goBack}
          className="flex items-center gap-2 p-2 rounded-lg text-gray-700 hover:bg-gray-200 hover:text-gray-900 w-full text-left"
        >
          <ArrowLeftCircle className="h-5 w-5 text-gray-700" />
          <span className="text-sm">
            {t('routes.admin-folder.timetable.sidebar.back')}
          </span>
        </button>

        <div className="sidebar">
          {navItems.map(({ label, component, icon: Icon }, index) => (
            <div key={component}>
              <button
                onClick={() => {
                  if (
                    component === 'DisplayTimetable' &&
                    !hasGeneratedTimetable
                  ) {
                    setActiveComponent('TimetableGenerator')
                    const event = new CustomEvent('show-toast', {
                      detail: {
                        title: t(
                          'routes.admin-folder.timetable.noTimetable.title'
                        ),
                        description: t(
                          'routes.admin-folder.timetable.noTimetable.message'
                        ),
                      },
                    })
                    window.dispatchEvent(event)
                  } else {
                    setActiveComponent(component)
                  }
                  setActiveSubComponent('')
                }}
                className={cn(
                  'flex items-center gap-2 p-2 rounded-lg w-full text-left',
                  activeComponent === component
                    ? 'bg-primary text-white'
                    : component === 'DisplayTimetable'
                      ? isLoading
                        ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        : hasGeneratedTimetable
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                      : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                )}
                data-tour={`sidebar-button-${index}`}
              >
                <Icon className="h-5 w-5" />
                <span>{label}</span>
                {component === 'DisplayTimetable' && !hasGeneratedTimetable && (
                  <span className="ml-2 text-xs bg-gray-300 text-gray-700 px-1 py-0.5 rounded">
                    {t('routes.admin-folder.timetable.sidebar.createFirst')}
                  </span>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
